/* Champ de recherche d'entreprise (annuaire officiel Sirene via data.gouv).
   Proxyé par le backend (GET /api/companies/search). Débounce 300 ms, liste
   déroulante, appelle `onPick` avec la société choisie. Réutilisé pour
   préremplir une fiche client, une facture, les paramètres entreprise et
   l'onboarding — même contrat que la recherche déjà en place dans le wizard. */
import { useEffect, useState } from 'react';
import { apiGet } from '../lib/api';
import { Field, useCombobox } from './ui';
import { Icon } from '../lib/icons';

export interface Company {
  siren: string;
  name: string;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  vatNumber: string | null;
  nafCode: string | null;
  active: boolean;
  /** false = diffusion protégée INSEE : nom/adresse blanchis par le serveur. */
  diffusible: boolean;
}

type Props = {
  label?: string;
  help?: string | null;
  placeholder?: string;
  onPick: (company: Company) => void;
  autoFocus?: boolean;
};

export function CompanySearch({
  label = 'Rechercher mon entreprise',
  help = 'Saisissez votre SIREN ou votre nom, puis choisissez dans la liste pour préremplir.',
  placeholder = 'Nom de l’entreprise ou SIREN',
  onPick,
  autoFocus,
}: Props) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Company[]>([]);
  const [searching, setSearching] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);

  // Recherche débounce contre l'annuaire Sirene. Une requête vide ou < 3
  // caractères court-circuite (et purge la liste) — ce qui couvre aussi le
  // reset après sélection (on remet le champ à vide dans pick()).
  useEffect(() => {
    const query = q.trim();
    if (query.length < 3) {
      setResults([]);
      setDropOpen(false);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      setSearching(true);
      apiGet<{ results: Company[] }>(`/api/companies/search?q=${encodeURIComponent(query)}`, ctrl.signal)
        .then((d) => {
          setResults(d.results);
          setDropOpen(true);
        })
        .catch(() => {
          if (!ctrl.signal.aborted) setResults([]);
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setSearching(false);
        });
    }, 300);
    return () => {
      ctrl.abort();
      clearTimeout(t);
    };
  }, [q]);

  function pick(co: Company) {
    setQ('');
    setResults([]);
    setDropOpen(false);
    onPick(co);
  }

  const combo = useCombobox({ open: dropOpen, items: results, onSelect: pick, onClose: () => setDropOpen(false) });

  return (
    <Field label={label} help={help ?? undefined}>
      <div className="input-wrap" style={{ position: 'relative' }}>
        <input
          className="ctrl"
          placeholder={placeholder}
          value={q}
          autoComplete="off"
          autoFocus={autoFocus}
          {...combo.inputProps}
          onChange={(ev) => setQ(ev.target.value)}
          onFocus={() => {
            if (results.length) setDropOpen(true);
          }}
          onBlur={() => setTimeout(() => setDropOpen(false), 120)}
        />
        <span className="ic-r" style={{ opacity: searching ? 0.5 : 1 }}>
          <Icon name="search" size={16} />
        </span>
        {dropOpen && results.length > 0 ? (
          <ul
            {...combo.listProps}
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              zIndex: 20,
              margin: 0,
              padding: 4,
              listStyle: 'none',
              background: 'var(--surface, #fff)',
              border: '1px solid var(--border-subtle, #E3E5E1)',
              borderRadius: 'var(--r-md, 10px)',
              boxShadow: '0 8px 28px rgba(0,0,0,.12)',
              maxHeight: 280,
              overflowY: 'auto',
            }}
          >
            {results.map((co, i) => (
              <li
                key={co.siren}
                {...combo.optionProps(i)}
                onMouseDown={() => pick(co)}
                style={{
                  padding: '9px 10px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: combo.active === i ? 'var(--surface-sunken, #F2F3F1)' : 'transparent',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {co.diffusible === false ? 'Entreprise non diffusible' : co.name || 'Nom non renseigné'}
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  SIREN {co.siren}
                  {co.city ? ' · ' + co.city : ''}
                  {!co.active ? ' · cessée' : ''}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Field>
  );
}
